import React from 'react';
// TODO update this to import from commons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import I18n from 'i18n-js';

import {
  Core_Listing_RecommendedShopsRowQuery,
  Core_Listing_RecommendedShopsRowQueryVariables,
} from '@reverbdotcom/commons/src/gql/graphql';
import ShopCard, { COMPONENT_NAME, ShopCardFragment } from './shop_card';
import { SHOP_PAGE } from './shared/url_helpers';

import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { ClickedRecommendedShopCardEvent, MParticleEvent, MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { RCGrid } from '@reverbdotcom/cadence/components';
import { shopRowItemRegionFilters, showItemPageShopRow } from './recommended_shops_row_utils';
import { isEU } from '@reverbdotcom/commons/src/geo';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';

interface IProps {
  listingId?: string;
  browsePageId?: string;
  brandSlug?: string;
  categorySlugs?: string[];
  shopSlugs?: string[];
  ssr?: boolean;
  title?: string;
}

type Shop = Core_Listing_RecommendedShopsRowQuery['listingsAggregationSearch']['shops'][0];

export const MINIMUM_SHOPS_TO_RENDER = 3;

function RecommendedShopsRow({
  listingId = undefined,
  browsePageId = undefined,
  brandSlug = undefined,
  categorySlugs = [],
  shopSlugs = [],
  ssr = false,
  title = I18n.t('discovery.shopCard.recommendedShopsTitle.default'),
}: IProps) {
  const user = useUser();
  const { loading, data } = useQuery<Core_Listing_RecommendedShopsRowQuery, Core_Listing_RecommendedShopsRowQueryVariables>(
    RECOMMENDED_SHOPS_ROW_QUERY,
    {
      ssr,
      variables: {
        brandSlugs: brandSlug ? [brandSlug] : [],
        categorySlugs,
        shopSlugs,
        ...shopRowItemRegionFilters(user),
      },
    },
  );

  const shops = loading || !data?.listingsAggregationSearch ? [] : data.listingsAggregationSearch.shops;

  const shouldRender = shops.length >= MINIMUM_SHOPS_TO_RENDER && (!!browsePageId || (!!listingId && showItemPageShopRow(user)));

  const trackedExperiments = [];

  if (!!listingId && isEU(user.countryCode)) {
    trackedExperiments.push(experiments.EU_SHOP_ROWS_V2);
  }

  const mparticleEvent: MParticleEvent = {
    componentName: 'RecommendedShopsRow',
    eventName: MParticleEventName.ComponentView,
    experiments: formatExperimentsForEventAttributes(user, trackedExperiments),
    shopCount: shops.length,
    browsePageId,
    listingId,
  };

  useViewTracking(mparticleEvent, !loading);

  if (!shouldRender) return null;

  return (
    <OverflowingRow
      title={title}
      collectionCount={shops.length}
    >
      <RCGrid singleRowOn="all" disableAndPulse={loading}>
        {shops.map((shop, index) => (
          <RCGrid.Item
            colSpan={{ desktop: 2, tablet: 3, mobile: 5 }}
            key={shop.id}
          >
            <CoreLink
              className="width-100"
              key={shop.id}
              to={shopLink(shop, brandSlug, categorySlugs)}
              clickEvent={buildClickEvent({
                shop,
                listingId,
                browsePageId,
                position: index,
                shopCount: shops.length,
              })}
            >
              <ShopCard shop={shop} />
            </CoreLink>
          </RCGrid.Item>
        ))}
      </RCGrid>
    </OverflowingRow>
  );
}

function optionalProductType(productTypeSlug: string) {
  if (productTypeSlug) {
    return { product_type: productTypeSlug };
  }
  return {};
}

function shopLink(shop: Shop, brandSlug: string, categorySlugs: string[]) {
  const [productTypeSlug] = categorySlugs;

  return SHOP_PAGE.expand({
    slug: shop.slug,
    query: {
      ...optionalProductType(productTypeSlug),
      make: brandSlug,
    },
  });
}

function buildClickEvent({
  shop,
  listingId,
  browsePageId,
  position,
  shopCount,
}: {
  shop: Shop,
  listingId: string,
  browsePageId: string,
  position: number,
  shopCount: number,
}): ClickedRecommendedShopCardEvent {
  return {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedRecommendedShopCard,
    listingId,
    browsePageId,
    position,
    shopCount,
    shopId: shop.id,
  };
}

export const RecommendedShopsRowListingFragment = gql`
  fragment RecommendedShopsRowListingFields on Listing {
    _id
    id
    brandSlug
    categories {
      _id
      slug
    }
  }
`;

const RECOMMENDED_SHOPS_ROW_QUERY = gql`
  query Core_Listing_RecommendedShopsRow(
    $itemRegion: String,
    $itemRegionRelation: reverb_search_ListingItemRegionRelation,
    $brandSlugs: [String],
    $categorySlugs: [String],
    $shopSlugs: [String]
  ) {
    listingsAggregationSearch(input: {
      withAggregations: [SHOP_ID],
      preferredSeller: true,
      brandSlugs: $brandSlugs,
      categorySlugs: $categorySlugs,
      itemRegion: $itemRegion,
      itemRegionRelation: $itemRegionRelation,
      shopSlugs: $shopSlugs
    }) {
      shops {
        _id
        id
        slug
        address {
          _id
          countryCode
        }
        ...ShopCardFields
      }
    }
  }
  ${ShopCardFragment}
`;

export default RecommendedShopsRow;
